// v0.6.37 — 抽出 openBookingModal helper (辉哥 9/29 23:46 反馈)
//   约课 modal (LessonEditorModal) 入口原本在 2 处重复:
//     - CalendarTab.openBooking() 桌面 dblclick / 移动 2-tap 时间格 (L475-490 旧版)
//       ⚠️ v0.8.12 起这条入口是右键菜单 / 长按菜单 (dblclick + 2-tap 已移除),
//          调用链变成 openSlotMenu → onBook/onEdit → openBooking, helper 签名不变。
//     - LessonsTab.openLessonEditor() "+ 新建预约" 按钮 (L96-110)
//   两处都是构造 modalAttrs + setTimeout(0) + try/catch + app.modal.show(LessonEditorModal, ...)
//   唯一区别是传参多少 (CalendarTab 传 start/end/room 预填, LessonsTab 不传)
//   抽出统一 helper, 行为不变, 未来加新约课入口都走这里.

import app from 'flarum/forum/app';
import LessonEditorModal from '../modal/LessonEditorModal';
import SelfBookingModal from '../modal/SelfBookingModal';

/**
 * 打开约课 modal 的统一入口
 *
 * 参数 (all optional, 不传走 LessonEditorModal oninit 默认):
 *   - mode: 'create' | 'edit' (默认 'create')
 *   - lesson: object | null (edit mode 必传)
 *   - start: ISO string (LessonEditorModal 默认 now())
 *   - end:   ISO string (LessonEditorModal 默认 null, 自己算)
 *   - room:  object | null (默认 null)
 *   - scheduleNodeId: number | null (v0.17.64 新增, 默认 null)
 *   - activeSchoolId: number | string | null
 *   - onClose: () => void (默认 noop)
 *   - lockDate: boolean (v0.18.35 新增, **默认 true = 日期锁死不可改**)
 *   - defaultTab: 'normal' | 'trial' (v0.18.38 新增, **默认 'normal' = 正课**)
 *
 * v0.18.38 — defaultTab 的语义 (辉哥 10/20 拍板):
 *   - **从「销售跟进」tab 的跟进记录里点「预约」进来的**: 用户是从一条**销售线索**
 *     出发去约课的, 约的是**体验课** (第一次上门), 不是正课 ⇒ 默认停在「体验课」tab。
 *   - **其它入口** (「约课管理」的「+ 新建预约」/ fc 课表右键「约课」): 默认仍是「正课」。
 *   - ⛔ **默认必须是 'normal' (正课)**: 将来新增入口时, 忘了传这个参数 ⇒ 默认安全 (正课),
 *     要默认体验课**必须显式**写 `defaultTab: 'trial'` —— 跟 `lockDate` 默认 true 是同一条口径。
 *   - ⚠️ **只认 'trial' 这一个值**: 别的值 (undefined / 'TRIAL' / '体验课' / null …) 一律归一为
 *     'normal'。弹窗里只有 'normal' / 'trial' 两个 tab, 传进来一个第三个值就是 bug, 宁可回落
 *     到安全的正课, 也不要让弹窗停在一个**不存在**的 tab 上 (那会让两份草稿都不显示)。
 *   - ⚠️ 跟 `lockDate` 一样, 这里是**显式白名单式**的 modalAttrs 构造 ⇒ **新标志位必须在这里加一行**
 *     才能透传到 LessonEditorModal —— 漏加的话 attrs 里就没有这个 key, 弹窗那边会走默认 (正课)。
 *
 * v0.18.35 — lockDate 的语义 (辉哥 10/20 拍板):
 *   - **从 fc 课表进入的约课**: 用户点的是课表上**具体某一格**, 那一格的日期就是这次约课的日期
 *     ⇒ 日期固定, 不可更改 (保持现状: 只读 chip)。显式传 `lockDate: true`。
 *   - **从其它入口进入的约课** (当前只有「约课管理」tab 的「+ 新建预约」): 用户没有指定某一天
 *     ⇒ 日期**可以选择**。显式传 `lockDate: false`。
 *   - ⛔ **默认必须是 true (锁死)**: 将来新增入口时, 忘了传这个参数 ⇒ 默认安全 (锁死),
 *     要可编辑**必须显式**写 `lockDate: false`。
 *   - ⚠️ 这里是**显式白名单式**的 modalAttrs 构造 (下面逐字段列出来), 所以**新标志位必须在这里加一行**
 *     才能透传到 LessonEditorModal —— 漏加的话 attrs 里就没有这个 key, 弹窗那边会走默认 (锁死)。
 *
 * v0.17.64 — 补 scheduleNodeId 的原因: 日历右键建课时调用方**已经知道用户点的是哪一行**,
 *   但改前这里只把 `start` 递下去, 节点 id 整个丢掉。弹窗于是只能拿 startTime 反查来显示
 *   下拉那一栏 (看着像选中了), form.scheduleNodeId 却恒为 null ⇒ 落库带 null ⇒ 后端只能按
 *   start_at 反推归属 ⇒ 重叠档一律判给 sort_order 第一档 (课存 10:30 却画在 10:15 那一行)。
 *   完整推导见 LessonEditorModal.backfillScheduleNodeIds() 的注释。
 *
 * setTimeout(0) + try/catch 跟 v0.6.7/v0.6.8 模式一致 (CalendarTab L484-490 + LessonsTab L103-109),
 *   防止 mithril race (modal close 后立刻 reopen 偶发 instance 复用问题, SOP 98).
 *
 * v0.19.16 — **按身份分流** (辉哥 10/28 09:1x 拍板:「1，A方案」)
 *   · **学员** ⇒ 弹批 35 的**学员版**「自主约课」弹窗 (`SelfBookingModal`)
 *     —— 入口**不新建**: 日历右键「约课」/ 约课管理「+ 新建预约」这两个**既有**入口
 *        学员本来就看得见 (生产实测), 本批只把「点进去弹什么」分流掉。
 *   · **员工** ⇒ 维持现有员工版 `LessonEditorModal`, **一个字都不改**。
 *   ⚠️ **分流必须在 `setTimeout` 之外判断** (同步读 `app.session.user`):
 *     放进 `setTimeout` 回调里 ⇒ 两个分支的时序不一致 (员工分支是「构造 attrs → 下一个 tick
 *     再 show」, 学员分支会变成「下一个 tick 才判断身份」), 容易出「学员点了弹员工版」。
 *   ⚠️ 判据 = `app.session.user.isStudent()` (与 `CalendarTab.isStudentView()` 同源);
 *     ⛔ 不用 `actorRole`、⛔ 不用 `canBookLessons()` (那是**权限位**, 学员组也有, 判不了身份)。
 */
export default function openBookingModal(opts) {
  if (opts === void 0) {
    opts = {};
  }
  // ── v0.19.16 — 学员分流 (同步判断, 必须在 setTimeout 之外) ──────────────────
  const sessionUser = app.session && app.session.user;
  const isStudent = !!(sessionUser && typeof sessionUser.isStudent === 'function' && sessionUser.isStudent());
  if (isStudent) {
    // ⛔ 只传 `date` / `onBooked` —— 学员版没有教室 / 时段节点 / 锁日期这些概念
    //   (⛔ `lesson` 非空 = edit 模式, 学员进不到这里: 学员没有 editBookedLessons / manageLessons)。
    const selfBookingAttrs = {
      // 'YYYY-MM-DD' 本地日历日; 不传 ⇒ 弹窗自己用今天 (约课管理入口就是这种)
      date: opts.date || null,
      onBooked: opts.onBooked || null
    };
    setTimeout(() => {
      try {
        app.modal.show(SelfBookingModal, selfBookingAttrs);
      } catch (e) {
        console.error('[booking-modal] open self-booking modal failed', e);
      }
    }, 0);
    return;
  }

  // ── 员工分支 (v0.6.37 起的原逻辑, **一个字都不改**) ─────────────────────────
  const modalAttrs = {
    mode: opts.mode || 'create',
    lesson: opts.lesson || null,
    start: opts.start || null,
    end: opts.end || null,
    room: opts.room || null,
    scheduleNodeId: opts.scheduleNodeId != null ? opts.scheduleNodeId : null,
    activeSchoolId: opts.activeSchoolId || null,
    onClose: opts.onClose || (() => {}),
    // v0.18.35 — 日期是否锁死。**默认 true (锁死)**, 要可编辑必须显式传 false。
    lockDate: opts.lockDate != null ? opts.lockDate : true,
    // v0.18.38 — 打开时默认停在哪个 tab。**默认 'normal' (正课)**, 只认 'trial'。
    //   别的值一律归一为 'normal' (理由见文件头 JSDoc)。
    defaultTab: opts.defaultTab === 'trial' ? 'trial' : 'normal'
  };
  setTimeout(() => {
    try {
      app.modal.show(LessonEditorModal, modalAttrs);
    } catch (e) {
      // 跟 CalendarTab '[calendar] open booking modal failed' + LessonsTab '[lessons-tab] open lesson editor failed' 合并 tag
      console.error('[booking-modal] open failed', e);
    }
  }, 0);
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/BookingModalHelper', openBookingModal);